import { FC, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,

  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle, CircleX, ExternalLink, Eye, EyeOff, TriangleAlert } from "lucide-react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import usePostData from "@/hooks/usePostData";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { cn } from "@/libs/utils";

// Reprise du step 2 de RegistrationForm (infos organisation)
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isStrongPassword = (password: string) =>
  password.length >= 8 &&
  /[a-z]/.test(password) &&
  /[A-Z]/.test(password) &&
  /\d/.test(password) &&
  /[!@#$%^&*(),.?":{}|<>]/.test(password);

const getDefaultValues = (funder: any) => ({
  firstname: funder?.firstname ?? "",
  lastname: funder?.lastname ?? "",
  email: funder?.email ?? "",
  emailRepresentant: "",
  password: "",
  confirmPassword: "",
  sendEmail: true,
});

const GiveAccessDialog: FC<{
  funder: any;
  open: boolean;

  onHandleOpen: (open: boolean) => void;
}> = ({ funder, open, onHandleOpen }) => {
  const { postData } = usePostData<any>();
  const isAccessAlreadyGranted = Boolean(funder?.access);
  const funderLabel = funder?.fullname || funder?.structureName || "ce financeur";
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [representants, setrepresentants] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);


  const formSchema = z
    .object({
      // Step 1: identité
      firstname: z.string().min(2, { message: "Le prénom est obligatoire" }),
      lastname: z.string().min(2, { message: "Le nom est obligatoire" }),

      // Step 1: email + mdp
      email: z
        .string()
        .regex(emailRegex, { message: "Email invalide" })
        .optional()
        .or(z.literal("")),
      emailRepresentant: z
        .string()
        .regex(emailRegex, { message: "Email invalide" })
        .optional()
        .or(z.literal("")),
      password: z.string().optional().or(z.literal("")),
      confirmPassword: z.string().optional().or(z.literal("")),
      sendEmail: z.boolean(),
    })
    // Vérifie que l'un des deux emails est rempli
    .refine((data) => (data?.email && data?.email.trim() !== "") || data.emailRepresentant?.trim() !== "", {
      message:
        "Veuillez renseigner au moins un email (email ou email représentant)",
      path: ["email"],
    })
    // Vérifie la correspondance des mots de passe
    .refine((data) => {
      if (!data.password && !data.confirmPassword) {
        return true;
      }

      return data.password === data.confirmPassword;
    }, {
      message: "Les mots de passe ne correspondent pas",
      path: ["confirmPassword"],
    });

  useEffect(() => {
    if (funder?.representants) {
      setrepresentants(
        funder.representants
          .filter((representant: any) => representant.email != "" && representant.email != null)
          .map((representant: any) => representant.email)
      );
    } else {
      setrepresentants([]);
    }
    setProjects(Array.isArray(funder?.projects) ? funder.projects : []);
  }, [funder]);

  type FunderAccessFormValues = z.infer<typeof formSchema>;
  type EmailAvailabilityResult = {
    available: boolean;
    existingAccount: boolean;
    existingUser: any;
  };

  const form = useForm<FunderAccessFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(funder),
  });
  const [pendingExistingFunderAccess, setPendingExistingFunderAccess] = useState<{
    values: FunderAccessFormValues;
    emailAvailability: EmailAvailabilityResult;
  } | null>(null);

  const password = form.watch("password") ?? "";

  useEffect(() => {
    form.reset(getDefaultValues(funder));
    setShowPassword(false);
    setShowPasswordConfirm(false);
  }, [funder, open, form]);

  const checkEmailAvailability = async (email: string) => {
    const token =
      localStorage.getItem("jwtToken") || sessionStorage.getItem("jwtToken");

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_ENDPOINT}/check-existing-user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            email,
            excludeId: funder?.id,
            targetRole: "ROLE_FUNDER",
          }),
        },
      );

      if (response.ok) {
        const result = await response.json().catch(() => null);
        return {
          available: true,
          existingAccount: Boolean(result?.existingUser),
          existingUser: result?.existingUser,
        };
      }

      const result = await response.json().catch(() => null);
      const message =
        result?.message || "Cette adresse email est deja utilisee";
      const fieldName =
        form.getValues("emailRepresentant")?.trim() !== ""
          ? "emailRepresentant"
          : "email";

      form.setError(fieldName, {
        type: "manual",
        message,
      });

      return {
        available: false,
        existingAccount: false,
        existingUser: null,
      };
    } catch {
      return {
        available: true,
        existingAccount: false,
        existingUser: null,
      };
    }
  };

  const validatePasswordBeforeSubmit = (
    password: string,
    confirmPassword: string,
    required: boolean,
  ) => {
    if (required && password.trim() === "") {
      form.setError("password", {
        type: "manual",
        message: "Le mot de passe est requis",
      });
      return false;
    }

    if (password.trim() === "" && confirmPassword.trim() === "") {
      return true;
    }

    if (!isStrongPassword(password)) {
      form.setError("password", {
        type: "manual",
        message: "Le mot de passe doit respecter les critères indiqués",
      });
      return false;
    }

    if (password !== confirmPassword) {
      form.setError("confirmPassword", {
        type: "manual",
        message: "Les mots de passe ne correspondent pas",
      });
      return false;
    }

    return true;
  };

  const submitFunderAccess = async (
    values: FunderAccessFormValues,
    emailAvailability: EmailAvailabilityResult,
    confirmExistingUser = false,
  ) => {
    const url = `${import.meta.env.VITE_IP_API}/api/access/financer/${funder?.id}`;
    const emailToUse = (values.emailRepresentant || values.email || "").trim();

    const password = values.password ?? "";
    const confirmPassword = values.confirmPassword ?? "";
    const passwordRequired = emailAvailability.existingAccount || (!emailAvailability.existingAccount && !isAccessAlreadyGranted);
    const isPasswordReady = validatePasswordBeforeSubmit(
      password,
      confirmPassword,
      passwordRequired,
    );
    if (!isPasswordReady) {
      return;
    }

    const data = {
      email: emailToUse,
      password,
      sendEmail: values.sendEmail === true,

      // nouvelles infos
      firstname: values.firstname.trim(),
      lastname: values.lastname.trim(),
      structureName: funder?.structureName ?? "",
      siret: funder?.siret ?? "",
      nif: funder?.nif ?? "",
      confirmExistingUser,
    };

    const result = await postData({ url, data });

    if (!result) {
      return;
    }

    setPendingExistingFunderAccess(null);
    onHandleOpen(false);
    toast.success(
      values.sendEmail === true
        ? isAccessAlreadyGranted
          ? "Les accès du financeur ont bien été mis à jour et un email a été envoyé."
          : "L'accès au financeur a bien été accordé et un email a été envoyé."
        : isAccessAlreadyGranted
          ? "Les accès du financeur ont bien été mis à jour. Aucun email n'a été envoyé."
          : "L'accès au financeur a bien été accordé. Aucun email n'a été envoyé."
    );

    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const GiveAccess = async (values: FunderAccessFormValues) => {
    const emailToUse = (values.emailRepresentant || values.email || "").trim();

    form.clearErrors(["email", "emailRepresentant"]);

    const emailAvailability = await checkEmailAvailability(emailToUse);
    if (!emailAvailability.available) {
      return;
    }

    if (emailAvailability.existingAccount) {
      const password = values.password ?? "";
      const confirmPassword = values.confirmPassword ?? "";
      const isPasswordReady = validatePasswordBeforeSubmit(
        password,
        confirmPassword,
        true,
      );
      if (!isPasswordReady) {
        return;
      }

      setPendingExistingFunderAccess({
        values,
        emailAvailability,
      });
      return;
    }

    await submitFunderAccess(values, emailAvailability);
  };

  useEffect(() => {
    setIsPasswordValid(isStrongPassword(password));
  }, [password]);

  return (
    <div>
      <Dialog open={open} onOpenChange={onHandleOpen}>
        <DialogContent
          className={cn(
            "sm:max-w-[900px]",
            pendingExistingFunderAccess && "pointer-events-none opacity-40"
          )}
        >
          <Form {...form}>
            <form onSubmit={form.handleSubmit(GiveAccess)}>
              <DialogHeader>
                <DialogTitle>
                  {isAccessAlreadyGranted
                    ? `Modifier l'accès de ${funderLabel}`
                    : `Donner un accès à ${funderLabel}`}
                </DialogTitle>
                <DialogDescription>
                  {isAccessAlreadyGranted
                    ? "En mettant à jour ses accès, le financeur recevra un email de confirmation pour se reconnecter à la plateforme."
                    : "En lui attribuant un accès financeur, il pourra se connecter à la plateforme dédiée et suivre en temps réel l’avancement des projets qu’il finance."}
                </DialogDescription>
              </DialogHeader>

              {/* Step 1: informations personnelles (Nom + Prénom) */}
              <div className="mt-4 space-y-4">
                <h4 className="text-base font-semibold text-gray-800">
                  Informations personnelles
                </h4>

                <div className="grid grid-cols-2 gap-2 w-full">
                  <FormField
                    control={form.control}
                    name="lastname"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nom</FormLabel>
                        <FormControl>
                          <Input placeholder="Nom" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="firstname"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Prénom</FormLabel>
                        <FormControl>
                          <Input placeholder="Prénom" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="sendEmail"
                  render={({ field }) => (
                    <FormItem className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                      <div className="flex items-start gap-3">
                        <FormControl>
                          <Checkbox
                            checked={field.value === true}
                            onCheckedChange={(checked) => field.onChange(checked === true)}
                            className="mt-1"
                          />
                        </FormControl>
                        <div className="space-y-2">
                          <FormLabel className="text-sm font-semibold text-amber-950">
                            Envoyer automatiquement les accès par email au financeur
                          </FormLabel>
                          <FormDescription className="text-amber-900">
                            Décochez cette option si vous préférez lui communiquer
                            manuellement l'identifiant et le mot de passe.
                          </FormDescription>
                          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-white/80 p-3 text-sm text-amber-950">
                            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                            <p className="m-0">
                              {field.value === true
                                ? "Attention : un email de confirmation d’accès sera envoyé automatiquement au financeur."
                                : "Aucun email ne sera envoyé. Vous devrez communiquer manuellement les accès au financeur."}
                            </p>
                          </div>
                        </div>
                      </div>
                    </FormItem>
                  )}
                />
              </div>

              {/* Step 1: accès (email + mdp) */}
              <div className="mt-6 space-y-4">
                <h4 className="text-base font-semibold text-gray-800">
                  Accès au compte
                </h4>

                <div className="grid grid-cols-2 gap-2 w-full items-center">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            placeholder=""
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              form.clearErrors(["email", "emailRepresentant"]);
                              if (e.target.value.trim() !== "") {
                                form.setValue("emailRepresentant", "");
                              }
                            }}
                          />
                        </FormControl>

                        {representants && representants.length > 0 ? (
                          <FormDescription className="flex flex-row gap-2 items-center justify-between">
                            <p className="m-0">
                              Saisissez l’adresse e-mail du financeur
                            </p>
                          </FormDescription>
                        ) : (
                          <FormDescription className="flex flex-row gap-2 items-center">
                            <TriangleAlert className=" text-red-600 w-4" />
                            <p className="m-0">
                              Vous devez connaître l’adresse e-mail du financeur
                              afin de pouvoir lui attribuer un accès.
                            </p>
                          </FormDescription>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {representants && representants.length > 0 && (
                    <div className="flex flex-row gap-2 items-center">
                      <p className="m-0 self-end">ou</p>
                      <div className="flex-1">
                        <FormField
                          control={form.control}
                          name="emailRepresentant"
                          render={({ field }) => (
                            <FormItem>
                              <Select
                                onValueChange={(value) => {
                                  field.onChange(value);
                                  form.clearErrors(["email", "emailRepresentant"]);
                                  if (value !== "") {
                                    form.setValue("email", "");
                                  }
                                }}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger className="w-full h-10 mt-4">
                                    <SelectValue placeholder="Sélectionnez un représentant" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectGroup>
                                    {representants?.map((representant, id) => (
                                      <SelectItem key={id} value={representant}>
                                        {representant}
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                              <FormDescription className=" ml-5 flex flex-row gap-2 items-center">
                                Sélectionnez l'email de son représentant
                              </FormDescription>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 w-full">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mot de passe</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              placeholder="Mot de passe"
                              {...field}
                              type={showPassword ? "text" : "password"}
                            />
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                setShowPassword(!showPassword);
                              }}
                              className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center"
                            >
                              {!showPassword ? <EyeOff /> : <Eye />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                        <PasswordStrengthCheck
                          password={password}
                          isPasswordValid={isPasswordValid}
                          className="mt-5"
                        />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirmation mot de passe</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              placeholder="Confirmation mot de passe"
                              {...field}
                              type={showPasswordConfirm ? "text" : "password"}
                            />
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                setShowPasswordConfirm(!showPasswordConfirm);
                              }}
                              className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center"
                            >
                              {!showPasswordConfirm ? <EyeOff /> : <Eye />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              {/* les projets  à financer  */}
              <div className="mt-6 space-y-4" >
                <h4 className="text-base font-semibold text-gray-800"> Projets à financer</h4>
                <div >

                  {
                    projects.map((project, index: number) => (
                      <div key={index}>
                        <Link

                          to={`/dashboard/project/${project.id}`}
                          className="inline-flex items-center gap-2 hover:underline text-[#1d5215] "
                          title="voir le projet"
                        >
                          <span className="truncate">{project.name}</span>
                          <ExternalLink className="w-4 h-4 opacity-70" />
                        </Link>
                      </div>

                    ))
                  }
                </div>
              </div>
              <DialogFooter className="flex flex-row gap-2 justify-end">
                <Button type="submit">
                  {isAccessAlreadyGranted
                    ? "Mettre à jour l'accès"
                    : "Confirmer"}
                </Button>

                <Button
                  className="bg-slate-500"
                  type="button"
                  onClick={() => {
                    onHandleOpen(false);
                  }}
                >
                  Annuler
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!pendingExistingFunderAccess}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setPendingExistingFunderAccess(null);
          }
        }}
      >
        <DialogContent
          className="z-[10001] sm:max-w-md"
          overlayClassName="z-[10000] bg-black/60"
        >
          <DialogHeader>
            <DialogTitle>Compte existant</DialogTitle>
            <DialogDescription>
              Cette adresse email est déjà utilisée comme Porteur de projet ou autre rôle.
              Voulez-vous aussi attribuer l'accès financeur à ce compte ? Les données nom,
              prénom, mot de passe et organisation ne seront pas modifiées.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingExistingFunderAccess(null)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (pendingExistingFunderAccess) {
                  submitFunderAccess(
                    pendingExistingFunderAccess.values,
                    pendingExistingFunderAccess.emailAvailability,
                    true,
                  );
                }
              }}
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GiveAccessDialog;

const PasswordStrengthCheck: FC<{
  password: string;
  isPasswordValid: boolean;
  className?: string;
}> = ({ password, isPasswordValid, className }) => {
  const checks = [
    { test: password.length >= 8, message: "Au moins 8 caractères" },
    { test: /[a-z]/.test(password), message: "Au moins une lettre minuscule" },
    { test: /[A-Z]/.test(password), message: "Au moins une lettre majuscule" },
    { test: /\d/.test(password), message: "Au moins un chiffre" },
    {
      test: /[!@#$%^&*(),.?\":{}|<>]/.test(password),
      message: "Au moins un caractère spécial",
    },
  ];

  return (
    <ul className={className}>
      <li className="mb-1 flex items-center gap-1">
        {isPasswordValid ? (
          <CheckCircle size="15" className="text-green-600" />
        ) : (
          <CircleX size="15" className="text-destructive" />
        )}{" "}
        <span className={`font-medium ${isPasswordValid && "text-green-600"}`}>
          Fiabilité du mot de passe :{" "}
        </span>
        {isPasswordValid ? (
          <span className="text-green-600">Fort</span>
        ) : (
          <span className="text-destructive">Faible</span>
        )}{" "}
      </li>
      {checks.map((check, index) => (
        <li key={index} className="mb-1 flex items-center gap-1">
          {check.test ? (
            <CheckCircle size="15" className="text-green-600" />
          ) : (
            <CircleX size="15" className="text-destructive" />
          )}
          <span className={`font-medium ${check.test ? "text-green-600" : ""}`}>
            {check.message}{" "}
          </span>
        </li>
      ))}
    </ul>
  );
};
